import { useMemo, useState, type FormEvent } from 'react'
import { useAuthStore } from '../state/authStore'

type AuthMode = 'login' | 'signup'

export function AuthScreen() {
  const setAuth = useAuthStore((state) => state.set)
  const storeError = useAuthStore((state) => state.error)
  const [mode, setMode] = useState<AuthMode>(() => {
    if (typeof window === 'undefined') return 'login'
    return window.location.pathname.startsWith('/auth/signup') ? 'signup' : 'login'
  })
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const oauthError = useMemo(() => {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    const error = params.get('error')
    if (error === 'oauth_failed') {
      return 'Google sign-in failed. Please try again.'
    }
    return null
  }, [])

  const returnTo = useMemo(() => {
    if (typeof window === 'undefined') return '/'
    return `${window.location.pathname}${window.location.search}${window.location.hash}`
  }, [])

  const activeError = localError ?? oauthError ?? storeError

  const submitLabel = mode === 'login' ? 'Sign in' : 'Create account'
  const toggleLabel = mode === 'login' ? 'Need an account? Create one' : 'Already have an account? Sign in'

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setLocalError(null)
    setAuth({ error: null })

    const endpoint = mode === 'login' ? '/auth/login' : '/auth/signup'
    const payload: Record<string, string> = {
      email,
      password,
    }
    if (mode === 'signup' && displayName.trim()) {
      payload.display_name = displayName.trim()
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        setLocalError(data?.error ?? 'Unable to sign in')
        return
      }
      if (data?.user) {
        setAuth({ status: 'authenticated', user: data.user, error: null })
      } else {
        setLocalError('Unable to load your profile')
      }
    } catch (error) {
      setLocalError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggle = () => {
    setLocalError(null)
    setAuth({ error: null })
    setMode((prev) => (prev === 'login' ? 'signup' : 'login'))
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-header">
          <p className="auth-kicker">AppGenie Dashboard</p>
          <h1 className="auth-title">{mode === 'login' ? 'Welcome back' : 'Create your account'}</h1>
          <p className="auth-subtitle">
            {mode === 'login'
              ? 'Sign in to pick up where you left off.'
              : 'Start your workspace with secure access.'}
          </p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {mode === 'signup' ? (
            <label className="auth-field">
              <span className="auth-label">Name</span>
              <input
                className="auth-input"
                type="text"
                name="display_name"
                autoComplete="name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="Jane Doe"
              />
            </label>
          ) : null}
          <label className="auth-field">
            <span className="auth-label">Email</span>
            <input
              className="auth-input"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              required
            />
          </label>
          <label className="auth-field">
            <span className="auth-label">Password</span>
            <input
              className="auth-input"
              type="password"
              name="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="password"
              required
            />
          </label>
          {activeError ? <p className="auth-error" role="alert">{activeError}</p> : null}
          <button className="auth-submit" type="submit" disabled={submitting}>
            {submitting ? 'Working...' : submitLabel}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <a
          className="auth-oauth-button"
          href={`/auth/google?return_to=${encodeURIComponent(returnTo)}`}
        >
          Continue with Google
        </a>

        <button className="auth-toggle" type="button" onClick={handleToggle}>
          {toggleLabel}
        </button>
      </div>
    </div>
  )
}
